import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Library, ArrowUpCircle } from 'lucide-react';
import { useLibraryBranding } from '@/lib/libraryBranding';
import { displayCategory } from '@/config/libraryCategories';

// Shown on an imported organisation template: where it came from (one committed pack head) and whether the library has moved on.
// Display is branded; origin_ref itself (machine pack_id, checksums, head revision) is left untouched.
export function LibraryProvenancePanel({ template }) {
  const brand = useLibraryBranding();
  const [update, setUpdate] = useState(null); // null = unknown/hidden, object = /updates row
  const ref = template?.origin_ref;
  useEffect(() => {
    if (!ref || template?.origin !== 'LIBRARY_IMPORT') return;
    api.get('/forms/library/updates').then(({ data }) => {
      setUpdate((data.items || []).find(u => u.template_id === template.id) || null);
    }).catch(() => setUpdate(null)); // 403 (no browse authority) → notice simply not shown; server is authoritative
  }, [ref, template?.id, template?.origin]);
  if (!ref || template?.origin !== 'LIBRARY_IMPORT') return null;
  const row = (k, v, tid, mono = true) => (
    <div className="flex justify-between gap-4 py-1 border-b border-dashed last:border-0 text-sm">
      <span className="text-muted-foreground">{k}</span><span className={`${mono ? 'font-mono text-xs' : ''} break-all text-right`} data-testid={tid}>{v ?? '—'}</span>
    </div>
  );
  return (
    <Card data-testid="library-provenance-panel">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Library className="w-4 h-4" />Template Library provenance</CardTitle></CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">Imported as an independent organisation copy. Editing this template never changes the library master.</p>
        {row('Pack', brand.libraryName, 'prov-pack-label', false)}
        {row('Category', displayCategory(ref.library_key), 'prov-category', false)}
        {row('Library key', ref.library_key, 'prov-library-key')}
        {row('Source version', ref.pack_version, 'prov-pack-version')}
        {row('Head revision', ref.head_revision, 'prov-head-revision')}
        {row('Master checksum', ref.library_checksum, 'prov-checksum')}
        {row('Imported', ref.imported_at, 'prov-imported-at')}
        {ref.repeat_import && <Badge variant="outline" className="mt-2 text-xs">Repeat import</Badge>}
        {update?.update_available && (
          <div role="status" className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="library-update-available">
            <ArrowUpCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              A newer library version ({update.active_pack_version}) is available. Your copy stays as it is; you may{' '}
              {update.active_library_id ? <Link className="underline" to={`/forms/library/${update.active_library_id}`} data-testid="library-update-link">review the current master</Link> : 'review the library'}{' '}
              and import a new copy if wanted.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
